/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import { Collapse } from 'react-bootstrap';

import AiManagementFilePreview from 'components/atoms/ai_management_file_preview';
import AiManagementDocumentCandidates from 'components/molecules/ai_management_document_candidates';
import AiManagementDocumentManagerSelect from 'components/molecules/ai_management_document_manager_select';
import { errorMessageOf } from 'hooks/useDocumentRequirements';
import type { ImportDocumentOptions } from 'services/actions/ai_management_document_requirements';
import { biddingFileHref } from 'services/actions/ai_management_files';
import { formatDate, isExpired } from 'services/ai_management_dates';
import {
  attachedFileFor,
  DOCUMENT_REQUIREMENT_STATUS,
  DocumentManagerDocument,
  DocumentRequirement,
  FILE_ORIGIN_LABEL,
  filesOutsideCandidates,
} from 'services/ai_management_document_requirements';
import { Container } from './styles';

interface CatalogProps {
  documents: DocumentManagerDocument[];
  loading: boolean;
  error: boolean;
  onOpen: () => void;
}

interface AiManagementDocumentRequirementCardProps {
  requirement: DocumentRequirement;
  open: boolean;
  catalog: CatalogProps;
  onToggle: () => void;
  onAddDocument: () => void;
  onImport: (documentId: string, options?: ImportDocumentOptions) => Promise<unknown>;
  onIgnore: () => Promise<unknown>;
  onRestore: () => Promise<unknown>;
  onRemoveFile: (fileId: number) => Promise<unknown>;
}

type Busy = 'import' | 'confirm' | 'ignore' | `remove-${number}` | null;

const subtitleOf = (requirement: DocumentRequirement) => {
  const isDeclaration = requirement.kind === 'declaration';

  if (requirement.status === 'ignored') {
    return requirement.ignoredBy
      ? `Exigência ignorada por ${requirement.ignoredBy}`
      : 'Exigência ignorada';
  }
  if (requirement.status === 'confirmed') {
    const label = isDeclaration ? 'Declaração confirmada' : 'Documento confirmado';
    return requirement.confirmedBy ? `${label} por ${requirement.confirmedBy}` : label;
  }
  if (requirement.lowConfidence)
    return 'Confirme no edital: exigência identificada com baixa confiança';
  if (requirement.analystNote) return requirement.analystNote;
  if (requirement.source === 'manual') return 'Exigência adicionada manualmente';
  return undefined;
};

const LEVEL_LABEL: Record<string, string> = { alta: 'Alta', média: 'Média' };

const renderOpinionBox = (content: React.ReactNode) => (
  <div className="alert alert-info mt-3 mb-0" role="note">
    <p className="d-flex align-items-center font-weight-bold mb-1">
      <i className="material-icons-outlined mr-2">smart_toy</i>
      Análise dos documentos encontrados no Gerenciar Documentos
    </p>
    {content}
  </div>
);

const renderAnalystOpinion = (opinion: NonNullable<DocumentRequirement['analystOpinion']>) => {
  const recommended = opinion.level !== 'revisar' && opinion.recommendedName;
  return renderOpinionBox(
    <>
      {recommended ? (
        <p className="mb-0">
          O Analista de Habilitação identificou que o documento &lsquo;{opinion.recommendedName}
          &rsquo; é o mais compatível com esta exigência (compatibilidade{' '}
          {LEVEL_LABEL[opinion.level] || opinion.level}).
          {opinion.justification && ` ${opinion.justification}`}
        </p>
      ) : (
        <p className="mb-0">
          O Analista de Habilitação não encontrou um documento que atenda com segurança. Esta
          exigência precisa de revisão manual.
        </p>
      )}
      {opinion.pendencies.length > 0 && (
        <ul className="mb-0 mt-2 pl-3" style={{ listStyleType: 'disc' }}>
          {opinion.pendencies.map((pendency) => (
            <li key={pendency}>{pendency}</li>
          ))}
        </ul>
      )}
    </>,
  );
};

const renderNoDocumentFound = (title: string) =>
  renderOpinionBox(
    <p className="mb-0">
      Não foi localizado nenhum documento correspondente ao requisito de {title} no repositório de
      documentos da empresa.
    </p>,
  );

const AiManagementDocumentRequirementCard: React.FC<AiManagementDocumentRequirementCardProps> = ({
  requirement,
  open,
  catalog,
  onToggle,
  onAddDocument,
  onImport,
  onIgnore,
  onRestore,
  onRemoveFile,
}) => {
  const [showExcerpt, setShowExcerpt] = useState(false);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [swapping, setSwapping] = useState(false);

  const ignored = requirement.status === 'ignored';
  const confirmed = requirement.status === 'confirmed';
  const statusInfo = DOCUMENT_REQUIREMENT_STATUS[requirement.status];
  const subtitle = subtitleOf(requirement);
  const bodyId = `document-requirement-${requirement.id}`;
  const text = requirement.description || requirement.edictExcerpt || requirement.title;
  const excerpt = requirement.description ? requirement.edictExcerpt : null;

  const opinion = requirement.analystOpinion;
  const candidates = opinion?.candidates ?? [];
  const showCandidates = candidates.length > 0;
  const noDocumentFound = requirement.noDocumentFound && !showCandidates;
  const attachedCandidateId =
    candidates.find((candidate) => !!attachedFileFor(requirement, candidate.id))?.id ?? null;
  const recommendedId = opinion && opinion.level !== 'revisar' ? opinion.recommendedId : null;
  // Vencido nunca vem pré-marcado nem confirma (decisão do PO, 02/10/2026).
  const notExpired = (id: string | null) => {
    const candidate = id ? candidates.find((c) => c.id === id) : undefined;
    return candidate && !isExpired(candidate.expiresOn) ? candidate.id : null;
  };
  const locked = confirmed && !!attachedCandidateId && !swapping;
  const effectiveSelectedId = locked
    ? attachedCandidateId
    : selectedId ?? notExpired(attachedCandidateId) ?? notExpired(recommendedId);
  const selectionIsCurrent = confirmed && effectiveSelectedId === attachedCandidateId;
  const canConfirm = !!notExpired(effectiveSelectedId) && !selectionIsCurrent && !ignored;
  const listedFiles = showCandidates ? filesOutsideCandidates(requirement) : requirement.files;

  // Exigência nova do servidor (confirmou, trocou, removeu): a seleção volta a ser a gravada.
  useEffect(() => {
    setSelectedId(null);
    setSwapping(false);
  }, [requirement.status, attachedCandidateId]);

  const run = (kind: Busy, action: () => Promise<unknown>, fallback: string) => {
    setBusy(kind);
    setErrorMessage('');
    action()
      .catch((err) => setErrorMessage(errorMessageOf(err, fallback)))
      .finally(() => setBusy(null));
  };

  const handleConfirm = () => {
    const documentId = effectiveSelectedId;
    if (!documentId) return;
    run(
      'confirm',
      () => onImport(documentId, { replace: true }),
      'Não foi possível confirmar o documento selecionado.',
    );
  };

  const handleSwap = (enable: boolean) => {
    setSwapping(enable);
    setSelectedId(null);
    setErrorMessage('');
  };

  const handleRemove = (fileId: number) => {
    setConfirmRemoveId(null);
    if (previewId === fileId) setPreviewId(null);
    run(`remove-${fileId}`, () => onRemoveFile(fileId), 'Não foi possível remover o arquivo.');
  };

  const renderFileActions = (file: DocumentRequirement['files'][number], previewing: boolean) => {
    if (confirmRemoveId === file.id) {
      return (
        <>
          <span className="mr-2">Remover arquivo?</span>
          <button
            type="button"
            className="btn btn-danger btn-sm mr-1"
            onClick={() => handleRemove(file.id)}
          >
            Remover
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setConfirmRemoveId(null)}
          >
            Cancelar
          </button>
        </>
      );
    }

    const removing = busy === `remove-${file.id}`;

    return (
      <>
        <a
          className="btn btn-outline-secondary btn-sm mr-1"
          href={biddingFileHref(file.downloadUrl)}
          download={file.name}
          target="_blank"
          rel="noreferrer"
          aria-label={`Baixar ${file.name}`}
        >
          <i className="material-icons">file_download</i>
        </a>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mr-1"
          onClick={() => setConfirmRemoveId(file.id)}
          disabled={removing}
          aria-label={`Remover ${file.name}`}
        >
          <i className="material-icons">{removing ? 'hourglass_empty' : 'delete_outline'}</i>
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setPreviewId(previewing ? null : file.id)}
          aria-expanded={previewing}
          aria-label={previewing ? 'Fechar pré-visualização' : 'Pré-visualizar arquivo'}
        >
          <i className="material-icons">{previewing ? 'expand_less' : 'expand_more'}</i>
        </button>
      </>
    );
  };

  const renderFiles = (files: DocumentRequirement['files']) => (
    <div className="requirement-files card mb-4">
      <table className="table mb-0">
        <thead className="thead-light">
          <tr>
            <th scope="col">Nome do arquivo</th>
            <th scope="col">Vencimento</th>
            <th scope="col" aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {files.map((file) => {
            const previewing = previewId === file.id;
            return (
              <React.Fragment key={file.id}>
                <tr>
                  <td className="file-name align-middle">
                    {file.name}
                    <small className="d-block text-muted">
                      {FILE_ORIGIN_LABEL[file.origin]}
                      {file.createdBy ? ` por ${file.createdBy}` : ''}
                    </small>
                  </td>
                  <td className="align-middle text-nowrap">
                    {file.expiresOn ? formatDate(file.expiresOn) : '—'}
                  </td>
                  <td className="file-actions align-middle text-right text-nowrap">
                    {renderFileActions(file, previewing)}
                  </td>
                </tr>
                {previewing && (
                  <tr className="file-preview-row">
                    <td colSpan={3} className="px-3 pb-3 pt-0">
                      <AiManagementFilePreview name={file.name} downloadUrl={file.downloadUrl} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderImportSelect = () => (
    <div className="mr-2 mb-2">
      <AiManagementDocumentManagerSelect
        documents={catalog.documents}
        loading={catalog.loading}
        error={catalog.error}
        onOpen={catalog.onOpen}
        disabled={ignored || (busy !== null && busy !== 'import')}
        busy={busy === 'import'}
        onSelect={(documentId) =>
          run(
            'import',
            () => onImport(documentId),
            'Não foi possível importar o documento do Gerenciar Documentos.',
          )
        }
      />
    </div>
  );

  const renderActions = () => {
    if (showCandidates && locked) {
      return (
        <button
          type="button"
          className="btn btn-primary mr-2 mb-2"
          onClick={() => handleSwap(true)}
          disabled={busy !== null}
        >
          Trocar documento
        </button>
      );
    }

    return (
      <>
        {showCandidates && (
          <button
            type="button"
            className="btn btn-success mr-2 mb-2"
            onClick={handleConfirm}
            disabled={!canConfirm || busy !== null}
          >
            {busy === 'confirm' ? 'Confirmando…' : 'Confirmar documento'}
          </button>
        )}
        {showCandidates && swapping && (
          <button
            type="button"
            className="btn btn-outline-secondary mr-2 mb-2"
            onClick={() => handleSwap(false)}
            disabled={busy !== null}
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          className="btn btn-outline-secondary mr-2 mb-2"
          onClick={onAddDocument}
          disabled={ignored || busy !== null}
        >
          Adicionar documento
        </button>
        {renderImportSelect()}
      </>
    );
  };

  return (
    <Container className={`card mb-3 ${open ? 'is-open' : ''} ${ignored ? 'is-ignored' : ''}`}>
      <div className="requirement-header d-flex align-items-center px-4 py-3">
        <div className="flex-grow-1 min-w-0 mr-3">
          <p className="requirement-title mb-0" title={requirement.title}>
            {requirement.title}
          </p>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
        <span className={`badge badge-${statusInfo.badge} flex-shrink-0 mr-3`}>
          {statusInfo.label}
        </span>
        <button
          type="button"
          className="requirement-toggle btn btn-secondary flex-shrink-0"
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={open ? 'Recolher exigência' : 'Expandir exigência'}
          onClick={onToggle}
        >
          <i className="material-icons">{open ? 'expand_less' : 'expand_more'}</i>
        </button>
      </div>

      <Collapse in={open}>
        <div id={bodyId}>
          <div className="px-4 pb-4">
            <p className="requirement-section-title mb-1">
              {requirement.kind === 'declaration' ? 'Declaração exigida' : 'Documento exigido'}
            </p>
            <p className="requirement-text text-muted mb-1">{text}</p>

            {excerpt && (
              <>
                <button
                  type="button"
                  className="btn btn-link p-0 d-inline-flex align-items-center"
                  aria-expanded={showExcerpt}
                  onClick={() => setShowExcerpt((prev) => !prev)}
                >
                  Conforme trecho do edital
                  <i className="material-icons ml-1">
                    {showExcerpt ? 'indeterminate_check_box' : 'add_box'}
                  </i>
                </button>
                {showExcerpt && (
                  <p className="requirement-excerpt text-primary font-italic mt-2 mb-0">
                    {excerpt}
                  </p>
                )}
              </>
            )}

            {noDocumentFound
              ? renderNoDocumentFound(requirement.title)
              : opinion && renderAnalystOpinion(opinion)}

            {(showCandidates || noDocumentFound) && (
              <AiManagementDocumentCandidates
                candidates={candidates}
                selectedId={effectiveSelectedId}
                selectable={!locked && !ignored && busy === null}
                busyId={busy === 'confirm' ? effectiveSelectedId : null}
                onSelect={setSelectedId}
              />
            )}

            <div className="mt-4">{listedFiles.length > 0 && renderFiles(listedFiles)}</div>

            {errorMessage && <div className="alert alert-danger py-2">{errorMessage}</div>}

            <p className="requirement-section-title mb-2">O que deseja fazer?</p>
            <div className="d-flex flex-wrap align-items-center">
              {renderActions()}
              <button
                type="button"
                className="btn btn-outline-secondary ml-auto mb-2"
                disabled={busy !== null}
                onClick={() =>
                  run(
                    'ignore',
                    ignored ? onRestore : onIgnore,
                    ignored
                      ? 'Não foi possível considerar a exigência.'
                      : 'Não foi possível ignorar a exigência.',
                  )
                }
              >
                {ignored ? 'Considerar exigência' : 'Ignorar exigência'}
              </button>
            </div>
          </div>
        </div>
      </Collapse>
    </Container>
  );
};

export default AiManagementDocumentRequirementCard;
